import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import { RequiredRoleLevel } from 'commons/src/models/commonEnums';
import { ActionButton } from 'commons/src/models/menuModels';
import { paths } from '../../../constants';
import MqttPublisherCreateForm from './MqttPublisherCreateForm';
import MqttPublisherDetailView from './MqttPublisherDetailView';
import { useMqttPublisherConfigs } from './useMqttPublisherConfigs';

const AddEditMqttPublisher = (): React.ReactElement => {
    const { clientId } = useParams<'clientId'>();
    const navigate = useNavigate();
    const { t: txt } = useTranslation();
    const { configs, loading, createConfig, createLoading, updateConfig, deleteConfig, updateLoading, deleteLoading } =
        useMqttPublisherConfigs();

    const isEditing = !!clientId;
    const config = isEditing ? configs.find(c => c.clientId === clientId) : undefined;

    useEffect(() => {
        if (isEditing && !loading && !config) {
            navigate(`/${paths.mqttPublisher}`);
        }
    }, [isEditing, loading, config, navigate]);

    const actionButton: ActionButton[] = [
        {
            onClick: (): void => navigate(`/${paths.mqttPublisher}`),
            testAttr: 'close-mqtt-publisher',
            title: 'MqttPublisher.Close',
            id: 'closeMqttPublisher',
            color: 'secondary',
            requiredRoleLevel: RequiredRoleLevel.ANY_ROLE,
            requiredGroupTypes: [],
        },
    ];

    return (
        <>
            <PageHeader title={isEditing ? txt('MqttPublisher.EditPublisher') : txt('MqttPublisher.CreatePublisher')} />
            <div className="page-wrapper__medium">
                <SubHeader actionButtons={actionButton} />
            </div>
            <ReactPlaceholder ready={!loading} customPlaceholder={mediumFormLoader}>
                {isEditing ? (
                    config && (
                        <MqttPublisherDetailView
                            config={config}
                            updateConfig={updateConfig}
                            deleteConfig={deleteConfig}
                            updateLoading={updateLoading}
                            deleteLoading={deleteLoading}
                        />
                    )
                ) : (
                    <MqttPublisherCreateForm createConfig={createConfig} createLoading={createLoading} />
                )}
            </ReactPlaceholder>
        </>
    );
};

export default AddEditMqttPublisher;
